/**
 * ApprovalFlowStep Component
 *
 * One numbered node of the approval flow: a header sentence, its controls, an "Upravit" button
 * and a body that opens beneath it. `ApprovalFlowChip` is the clickable part of the sentence.
 */
import type { ReactNode } from 'react';
import { Button, cn, Icon } from '@insio/ui-toolkit';

export interface ApprovalFlowChipProps {
  label: string;
  /** The step's body is open — the chip is filled. */
  isActive: boolean;
  onClick: () => void;
  /** Id of the body the chip opens. */
  controlsId: string;
  testId?: string;
}

/** The variable part of a step's sentence; it opens the body that sets it. */
export function ApprovalFlowChip({
  label,
  isActive,
  onClick,
  controlsId,
  testId
}: ApprovalFlowChipProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-expanded={isActive}
      aria-controls={controlsId}
      data-testid={testId}
      // Same border, padding and radius in both states, so filling it never shifts the line.
      className={cn(
        'rounded-md border px-xs text-left font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-1',
        isActive
          ? 'border-primary-base bg-primary-base text-white'
          : 'border-dashed border-primary-base/40 bg-primary-base/5 text-foreground-base hover:bg-primary-base/10'
      )}
    >
      {label}
    </button>
  );
}

export interface ApprovalFlowStepProps {
  /** The step number, or `end` for the closing check mark. */
  marker: number | 'end';
  /** The sentence the step reads as. */
  header: ReactNode;
  /** Controls beside the sentence, e.g. a switch. */
  actions?: ReactNode;
  /** Greyed out — the step does not run. */
  isMuted?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  editLabel?: string;
  /** Id of the body, named by the chip that opens it. */
  bodyId?: string;
  children?: ReactNode;
  testId?: string;
}

export function ApprovalFlowStep({
  marker,
  header,
  actions,
  isMuted = false,
  isOpen = false,
  onToggle,
  editLabel,
  bodyId,
  children,
  testId
}: ApprovalFlowStepProps) {
  return (
    <li className='relative' data-testid={testId}>
      <span
        aria-hidden='true'
        className={cn(
          'absolute -left-7 top-2.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white',
          marker === 'end'
            ? 'bg-success-solid'
            : isMuted
              ? 'bg-foreground-disabled'
              : 'bg-primary-base'
        )}
      >
        {marker === 'end' ? <Icon name='check' className='h-3 w-3' /> : marker}
      </span>
      <div className='rounded-xl border border-border-base bg-background-input px-md py-sm'>
        <div className='flex min-h-7 flex-wrap items-center justify-between gap-sm'>
          <p
            className={cn(
              'min-w-0 flex-1 text-sm font-semibold leading-relaxed',
              isMuted ? 'text-foreground-disabled' : 'text-foreground-base'
            )}
          >
            {header}
          </p>
          {(actions || onToggle) && (
            <div className='flex items-center gap-sm'>
              {actions}
              {onToggle && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={onToggle}
                  aria-expanded={isOpen}
                  testId={testId ? `${testId}-edit` : undefined}
                >
                  {editLabel}
                </Button>
              )}
            </div>
          )}
        </div>
        {children && (
          // Kept mounted while closed, so a half-picked chain survives closing its step.
          <div id={bodyId} hidden={!isOpen} className='pt-sm'>
            {children}
          </div>
        )}
      </div>
    </li>
  );
}
